/**
 * PartsBlockedWidget — jobs flagged waiting-for-parts, with the linked purchase
 * order's ETA if the parts are on order (or a "not ordered" marker otherwise).
 * Connects the parts pipeline to the work queue so blockers are visible.
 */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import type { PartsBlockedItem } from '@/hooks/useDashboard';
import { useOpenWorkOrder } from '@/hooks/useWorkOrderWindow';
import { intlLocale } from '@/utils/locale';
import { handlePlainLeftClick } from './dashboardLinkClick';

interface PartsBlockedWidgetProps {
  items?: PartsBlockedItem[];
}

export function PartsBlockedWidget({ items = [] }: PartsBlockedWidgetProps) {
  const { t } = useTranslation('dashboard');
  const openWorkOrder = useOpenWorkOrder();

  const etaChip = (item: PartsBlockedItem) => {
    if (!item.hasOrder) {
      return (
        <Chip
          label={t('partsBlocked.notOrdered')}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
      );
    }
    const label = item.expectedDeliveryDate
      ? new Date(item.expectedDeliveryDate).toLocaleDateString(intlLocale(), {
          weekday: 'short',
          day: 'numeric',
          month: 'numeric',
        })
      : t('partsBlocked.noEta');
    return (
      <Chip label={label} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BuildCircleIcon color="warning" />
          <Typography variant="h6">{t('partsBlocked.title')}</Typography>
          {items.length > 0 && (
            <Chip label={items.length} size="small" color="warning" sx={{ ml: 'auto' }} />
          )}
        </Box>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('partsBlocked.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.bookingId}
                component={RouterLink}
                to={`/workorders/${item.bookingId}`}
                onClick={(event) => {
                  handlePlainLeftClick(event, () => openWorkOrder(item.bookingId));
                }}
                sx={{ borderRadius: 1, mb: 0.5, px: 1, gap: 1, textDecoration: 'none', color: 'inherit' }}
              >
                {etaChip(item)}
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.vehicleRegNumber || item.customerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.serviceType}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    item.partName ? (
                      <Typography variant="caption" color="text.secondary">
                        {item.partName}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
